// GITEX SINGLE SOURCE OF TRUTH for all database paths.
// Nothing in the codebase may hardcode a Firestore collection or an RTDB path;
// every reader/writer imports from here. This prevents path drift and the
// write collisions that come from typo'd paths.

/** Validates a path segment so it cannot break out of its namespace. */
function seg(value: string): string {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) {
    throw new Error(`Invalid db path segment: "${value}"`);
  }
  return value;
}

/**
 * Firebase RTDB paths — high-frequency, low-cost. Used for live ticks,
 * the engine heartbeat/state, and the control plane (kill switch).
 * RTDB free tier: 100 concurrent connections, 1 GB stored, 10 GB/mo egress.
 */
export const rtdb = {
  /** Latest market tick for a symbol. Written by the engine each cycle. */
  tick: (symbol: string): string => `ticks/${seg(symbol)}`,
  /** Engine liveness + last-cycle metadata. */
  engineState: (): string => 'engine/state',
  /** Control plane read every cycle. */
  killSwitch: (): string => 'control/killSwitch',
  /** Rolling daily realized PnL percentage, used by the risk breaker. */
  dailyPnlPct: (): string => 'control/dailyPnlPct',
  /** Application error log sink (mirrors Sentry; cheap to query in-app). */
  appErrors: (): string => 'appErrors',
} as const;

/**
 * Firestore collections — durable, queryable, rule-protected. Used for
 * config, executed trades, open positions, signals and audit.
 * Firestore free tier: 50k reads / 20k writes / 20k deletes per day.
 */
export const firestore = {
  /** Per-user profile docs. */
  user: (uid: string): string => `users/${seg(uid)}`,
  /** Bot configuration per user (strategy params, risk params, enabled pairs). */
  botConfig: (uid: string): string => `users/${seg(uid)}/botConfig/active`,
  /** Executed trades (append-only audit). */
  trades: (uid: string): string => `users/${seg(uid)}/trades`,
  trade: (uid: string, tradeId: string): string => `users/${seg(uid)}/trades/${seg(tradeId)}`,
  /** Currently open positions. */
  positions: (uid: string): string => `users/${seg(uid)}/positions`,
  position: (uid: string, symbol: string): string =>
    `users/${seg(uid)}/positions/${seg(symbol)}`,
  /** Generated signals (audit of why the engine acted). */
  signals: (uid: string): string => `users/${seg(uid)}/signals`,
  /** Daily risk state snapshot (equity, realized PnL, breaker status). */
  riskState: (uid: string, day: string): string => `users/${seg(uid)}/riskState/${seg(day)}`,
} as const;
